import { useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useNavigate, useParams } from 'react-router-dom';
import { cloudidentity_v1 } from '@googleapis/cloudidentity';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import GroupMembershipExpirationChip from './group-membership-expiration-chip';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DirectoryGroup } from '../../../../../types';
import {
  getCloudIdentityGroupMembers,
  getGroupLabelsQuery,
  groupSecuritySettingsQuery,
  UserKeys,
} from '../../../../api/users-api';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { Messages } from '../../../../utils/message-constants';
import ChangeGroupCIMemberRoleDialog from './change-group-ci-member-role-dialog';
import CloneGroupCIMembersDialog from './clone-group-ci-members-dialog';
import BulkAddGroupCIMembersDialog from './bulk-add-group-ci-members-dialog';
import DeleteGroupMembersDialog from './delete-group-members-dialog';
import { pagesConfig } from '../../../../shared/pages-config';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import CreateButton from '../../../../shared/buttons/create-button';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import GroupRemove from '@mui/icons-material/GroupRemove';
import Groups from '@mui/icons-material/Groups';
import Person from '@mui/icons-material/Person';
import Visibility from '@mui/icons-material/Visibility';

interface GroupCIMembershipGridProps {
  group: DirectoryGroup;
}

function getRolePrecedence(roleName: string | null | undefined): number {
  switch (roleName) {
    case 'OWNER':
      return 3;
    case 'MANAGER':
      return 2;
    case 'MEMBER':
      return 1;
    default:
      return 0;
  }
}

export function getHighestRole(
  roles: cloudidentity_v1.Schema$MembershipRole[] | undefined | null
): cloudidentity_v1.Schema$MembershipRole | null {
  if (!roles || roles.length === 0) {
    return null;
  }

  return roles.reduce((highest, current) =>
    getRolePrecedence(current.name) > getRolePrecedence(highest.name)
      ? current
      : highest
  );
}

const GroupCIMembershipGrid = ({ group }: GroupCIMembershipGridProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openAddMembers, setOpenAddMembers] = useState(false);
  const [openDeleteMembersFromGroup, setOpenDeleteMembersFromGroup] =
    useState(false);
  const [openCloneGroupMembers, setOpenCloneGroupMembers] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openChangeRoles, setOpenChangeRoles] = useState(false);
  const { groupEmail } = useParams() as { groupEmail: string };

  const { directoryRoles, changeLogRoles, customerId } = useUserSettings();

  const { data: groupLabels } = useQuery({
    ...getGroupLabelsQuery(groupEmail, group?.id ?? ''),
  });

  const { data: securitySettings } = useQuery({
    ...groupSecuritySettingsQuery(groupEmail, group?.id ?? ''),
  });

  const {
    data: cloudIdentityGroupMembers,
    isLoading: isCloudIdentityLoading,
    isFetching: isCloudIdentityFetching,
  } = useQuery({
    ...getCloudIdentityGroupMembers(groupEmail, group?.id ?? ''),
  });

  const hasSecurityLabel =
    groupLabels?.['cloudidentity.googleapis.com/groups.security'] !== undefined;

  const hasSecuritySettings = securitySettings?.memberRestriction !== undefined;
  const addAllMembersDisabled = hasSecurityLabel || hasSecuritySettings;

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 2,
      display: 'flex',
      valueGetter: (_value, row) => row.preferredMemberKey?.id || customerId,
      maxWidth: 300,
      sortable: true,
      renderCell: (params) => {
        const emailDisplay =
          !params.value || params.value === customerId
            ? 'All current and future users within domain'
            : params.value;
        const avatar = (
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <Typography variant="body2">{emailDisplay}</Typography>
            {params.row.type === 'GROUP' ? (
              <Tooltip arrow title="View Group">
                <span>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/${pagesConfig.groups.link}/${params.value}`);
                    }}
                  >
                    <Visibility />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
        );
        return avatar;
      },
    },
    {
      field: 'roles',
      headerName: 'Role',
      flex: 1,
      display: 'flex',
      maxWidth: 100,
      valueGetter: (value: cloudidentity_v1.Schema$MembershipRole[] | null) => {
        const highestRole = getHighestRole(value);
        const roleValue = highestRole ? highestRole.name : '';
        return roleValue
          ? roleValue.charAt(0).toUpperCase() + roleValue.slice(1).toLowerCase()
          : '';
      },
      type: 'singleSelect',
      valueOptions: ['Owner', 'Manager', 'Member'],
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      display: 'flex',
      type: 'singleSelect',
      maxWidth: 100,
      valueOptions: ['USER', 'GROUP'],
      renderCell: (params) => {
        const avatar = (
          <Stack flexDirection="row" alignItems="center" gap={1}>
            {params.row.type === 'GROUP' ? (
              <Tooltip arrow title="Group">
                <Groups />
              </Tooltip>
            ) : (
              <Tooltip arrow title="User">
                <Person />
              </Tooltip>
            )}
          </Stack>
        );
        return avatar;
      },
    },
    {
      field: 'createTime',
      headerName: 'Date Created',
      type: 'dateTime',
      valueGetter: (params: number) => {
        return new Date(params);
      },
      valueFormatter: (params: Date) => {
        return params.toLocaleString();
      },
      flex: 1,
    },
    {
      field: 'updateTime',
      headerName: 'Date Updated',
      type: 'dateTime',
      valueGetter: (params: number) => {
        return new Date(params);
      },
      valueFormatter: (params: Date) => {
        return params.toLocaleString();
      },
      flex: 1,
    },
    {
      field: 'expiryTime',
      headerName: 'Membership Expiry (UTC)',
      flex: 1,
      renderCell: (params) => {
        const currentExpirationTime = params.row.roles[0].expiryDetail
          ? params.row.roles[0].expiryDetail.expireTime
          : '';
        const highestRole = getHighestRole(params.row.roles);
        const memberName = params.row.name.split('/');
        // The domain-wide "all current and future users" membership doesn't
        // always resolve a preferredMemberKey from the Cloud Identity API -
        // fall back to customerId (as elsewhere in this file) so this cell
        // reliably shows "Not applicable" for it instead of an editable
        // expiration chip with a blank member key.
        const resolvedMemberKey =
          params.row.preferredMemberKey?.id || customerId;
        return customerId != resolvedMemberKey ? (
          <Tooltip
            arrow
            title={
              !isAbleToEditGroupLabels
                ? Messages.Errors.Permission.Acting(
                    DirectoryRoles.EditGroupMemberExpiry.name
                  )
                : ''
            }
          >
            <span>
              <GroupMembershipExpirationChip
                groupEmail={groupEmail}
                memberEmail={resolvedMemberKey ?? ''}
                currentExpirationTime={currentExpirationTime}
                groupId={memberName[1]}
                membershipId={memberName[3]}
                memberRole={highestRole?.name}
              />
            </span>
          </Tooltip>
        ) : (
          <Chip label="Not applicable" size="small" disabled />
        );
      },
    },
  ];

  const isAbleToEditGroupLabels = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.EditGroupMemberExpiry.value
  );

  const isAbleToAddMembers = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.AddMemberToGroup.value
  );

  const AddMemberRoleTooltip = !isAbleToAddMembers
    ? Messages.Errors.Permission.Acting(DirectoryRoles.AddMemberToGroup.name)
    : 'Add Member';

  const CloneMemberRoleTooltip = !isAbleToAddMembers
    ? Messages.Errors.Permission.Acting(DirectoryRoles.AddMemberToGroup.name)
    : 'Add Members From Group';

  const EditMemberRoleTooltip = !isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.EditGroupMemberRole.value
  )
    ? Messages.Errors.Permission.Acting(DirectoryRoles.EditGroupMemberRole.name)
    : 'Edit Roles';

  const RemoveMemberRoleTooltip = !isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.RemoveMemberFromGroup.value
  )
    ? Messages.Errors.Permission.Acting(
        DirectoryRoles.RemoveMemberFromGroup.name
      )
    : 'Delete';

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        primaryButtons={
          <CreateButton
            onClick={() => setOpenAddMembers(true)}
            disabled={!isAbleToAddMembers}
            label="Add Member"
            tooltipLabel={AddMemberRoleTooltip}
          />
        }
        secondaryButtons={
          <>
            <SharedIconButton
              variant="edit"
              onClick={() => setOpenChangeRoles(true)}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  directoryRoles,
                  DirectoryRoles.EditGroupMemberRole.value
                ) ||
                selectedRows.includes('*')
              }
              tooltipLabel={EditMemberRoleTooltip}
            />
            <SharedIconButton
              variant="cloneMember"
              onClick={() => setOpenCloneGroupMembers(true)}
              disabled={
                !isAbleToSeeUIElement(
                  directoryRoles,
                  DirectoryRoles.AddMemberToGroup.value
                )
              }
              tooltipLabel={CloneMemberRoleTooltip}
            />
            <SharedIconButton
              variant="delete"
              onClick={() => setOpenDeleteMembersFromGroup(true)}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  directoryRoles,
                  DirectoryRoles.RemoveMemberFromGroup.value
                )
              }
              tooltipLabel={RemoveMemberRoleTooltip}
            />
            <SharedIconButton
              variant="refresh"
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.GroupMembersCloudIdentity, groupEmail],
                });
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.DirectoryGroup, groupEmail],
                });
              }}
              tooltipLabel="Refresh"
            />
          </>
        }
      ></SharedToolbar>
    );
  }

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Members', <GroupRemove />),
        }}
        getRowId={(row) => row.name ?? ''}
        rows={cloudIdentityGroupMembers || []}
        columns={columns}
        loading={isCloudIdentityLoading || isCloudIdentityFetching}
        disableColumnSelector
        checkboxSelection
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
      {selectedRows.length > 0 && openDeleteMembersFromGroup && (
        <DeleteGroupMembersDialog
          open={openDeleteMembersFromGroup}
          handleClose={() => setOpenDeleteMembersFromGroup(false)}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          groupEmail={groupEmail}
          group={group}
          memberList={[]}
          changeLogRoles={changeLogRoles}
          userView={false}
          isCloudIdentityEnabled={true}
          cloudIdentityGroupMembers={cloudIdentityGroupMembers}
        />
      )}
      {selectedRows.length > 0 && openChangeRoles && (
        <ChangeGroupCIMemberRoleDialog
          open={openChangeRoles}
          handleClose={() => setOpenChangeRoles(false)}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          groupEmail={groupEmail}
          group={group}
          memberships={cloudIdentityGroupMembers || []}
          changeLogRoles={changeLogRoles}
          customerId={customerId ?? ''}
        />
      )}
      {openCloneGroupMembers && (
        <CloneGroupCIMembersDialog
          open={openCloneGroupMembers}
          handleClose={() => setOpenCloneGroupMembers(false)}
          groupName={group.name}
          groupEmail={groupEmail}
          groupId={group?.id ?? ''}
          changeLogRoles={changeLogRoles}
          customerId={customerId ?? ''}
          hasSecurityLabelsOrSettings={addAllMembersDisabled}
        />
      )}
      {openAddMembers && (
        <BulkAddGroupCIMembersDialog
          open={openAddMembers}
          handleClose={() => setOpenAddMembers(false)}
          groupEmail={groupEmail}
          memberList={cloudIdentityGroupMembers || []}
          changeLogRoles={changeLogRoles}
          parentGroupId={group?.id ?? ''}
          addAllMembersDisabled={addAllMembersDisabled}
          directoryRoles={directoryRoles}
        />
      )}
    </>
  );
};

export default GroupCIMembershipGrid;
